import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';

/**
 * Automation activity log (C360-3154) — what the rules actually produced, as
 * opposed to how they are configured. One entry per automated clip group.
 */
/** What GameMatchup reads off each side. */
interface ClipRuleActivityTeam {
  id: number;
  abbreviation: string | null;
  teamColors: { type: string; hex_color: string }[];
  logoUrl: string | null;
}

export interface ClipRuleActivityEntry {
  clipGroupId: number;
  createdAt: string;
  name: string;
  eventType: string | null;
  period: string | null;
  gameTime: string | null;
  playbyplayId: string | null;
  rule: { id: number; name: string } | null;
  game: {
    id: number;
    uniqueId: string | null;
    name: string;
    homeTeamColor: string | null;
    awayTeamColor: string | null;
    homeTeam: ClipRuleActivityTeam;
    awayTeam: ClipRuleActivityTeam;
  } | null;
  clips: {
    total: number;
    ready: number;
    pending: number;
    failed: number;
    canceled: number;
    items: { id: number; status: string | null }[];
  };
}

interface ClipRuleActivityResponse {
  success: boolean;
  count: number;
  activity: ClipRuleActivityEntry[];
}

export const CLIP_RULE_ACTIVITY_QUERY_KEY = ['ClipRuleActivity'];

export const useClipRuleActivity = (options: { ruleId?: number; enabled?: boolean } = {}) =>
  useQuery({
    queryKey: [...CLIP_RULE_ACTIVITY_QUERY_KEY, options.ruleId ?? null],
    queryFn: async () => {
      // http-utils' second argument transforms the axios config, so the filter
      // goes in the URL rather than a `params` object.
      const query = options.ruleId ? `?ruleId=${options.ruleId}` : '';
      const { data } = await get<ClipRuleActivityResponse>(`/v1.0/clip-rules/activity${query}`);
      return data;
    },
    enabled: options.enabled ?? true,
    // Clips move through export states on their own, so a stale log is
    // misleading rather than merely old.
    refetchInterval: 15 * 1000,
    staleTime: 10 * 1000,
  });

export default useClipRuleActivity;
